import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { components } from "@jbnl/api-client";
import i18n from "@/i18n";
import { api } from "@/lib/api";
import { vereist } from "@jbnl/shared-ui";

export const functiesKey = ["beheer", "functies"] as const;
export const stamKlassenKey = ["beheer", "stamgegevens", "klassen"] as const;
export const stamNiveausKey = ["beheer", "stamgegevens", "niveaus"] as const;
export const bezettingKey = ["beheer", "bezetting"] as const;
export const speelschemasKey = [
  "beheer",
  "stamgegevens",
  "speelschemas",
] as const;
export const bevoegdhedenKey = (persoonId: string) =>
  ["beheer", "bevoegdheden", persoonId] as const;

export type Functie = components["schemas"]["FunctieDto"];
export type FunctieScope = components["schemas"]["FunctieScope"];
export type FunctieNoodzaak = components["schemas"]["FunctieNoodzaak"];
export type KlasseStam = components["schemas"]["KlasseStamDto"];
export type NiveauStam = components["schemas"]["NiveauStamDto"];
export type Bezetting = components["schemas"]["BezettingDto"];
export type Leeftijdsgroep = components["schemas"]["Leeftijdsgroep"];
export type StandaardSpeelschema =
  components["schemas"]["StandaardSpeelschemaDto"];
export type StandaardRegel =
  components["schemas"]["StandaardSpeelschemaregelDto"];

/** The API serialises `FunctieScope` as a number, so the SPA names the two digits once. */
export const SCOPE = { wedstrijdbaan: 1, wedstrijddag: 2 } as const;

export function scopeLabel(scope: number) {
  return scope === SCOPE.wedstrijdbaan
    ? i18n.t("stamgegevens:scope.wedstrijdbaan")
    : i18n.t("stamgegevens:scope.wedstrijddag");
}

/**
 * `1` must be filled, `2` may be. Serialised as a number for the same reason SCOPE is. A functie
 * is optioneel because of what the job is, not because of any klasse or niveau - the stage
 * functies are the two (ADR-0061).
 */
export const NOODZAAK = { verplicht: 1, optioneel: 2 } as const;

/** `1` is junioren en `2` aspiranten, for the same reason. */
export const LEEFTIJDSGROEP = { junioren: 1, aspiranten: 2 } as const;

export function leeftijdsgroepLabel(groep: number) {
  return groep === LEEFTIJDSGROEP.junioren
    ? i18n.t("stamgegevens:leeftijdsgroep.junioren")
    : i18n.t("stamgegevens:leeftijdsgroep.aspiranten");
}

/**
 * Shown wherever the bare name appears, because *junioren* means an age band only to somebody who
 * already knows JBNL. A klasse name carries its own band, so those are left alone.
 */
export function leeftijdsgroepJaren(groep: number) {
  return groep === LEEFTIJDSGROEP.junioren
    ? i18n.t("stamgegevens:leeftijdsgroep.jarenJunioren")
    : i18n.t("stamgegevens:leeftijdsgroep.jarenAspiranten");
}

/** The name with its years, for a line that has no room for a second one. */
export function leeftijdsgroepMetJaren(groep: number) {
  return groep === LEEFTIJDSGROEP.junioren
    ? i18n.t("stamgegevens:leeftijdsgroep.metJarenJunioren")
    : i18n.t("stamgegevens:leeftijdsgroep.metJarenAspiranten");
}

/** Same shape as the wedstrijden hooks: the RFC 7807 title is the sentence to show. */
function problem(error: unknown, response: Response, fallback: string) {
  const title = (error as { title?: string } | undefined)?.title;
  return new Error(title ?? `${fallback} (${response.status}).`);
}

export function useFuncties() {
  return useQuery({
    queryKey: functiesKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/beheer/functies");
      if (!data)
        throw problem(error, response, i18n.t("stamgegevens:errors.functies"));
      return data;
    },
  });
}

export function useStamKlassen() {
  return useQuery({
    queryKey: stamKlassenKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        "/api/beheer/stamgegevens/klassen",
      );
      if (!data)
        throw problem(error, response, i18n.t("stamgegevens:errors.klassen"));
      return data;
    },
  });
}

export function useStamNiveaus() {
  return useQuery({
    queryKey: stamNiveausKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        "/api/beheer/stamgegevens/niveaus",
      );
      if (!data)
        throw problem(error, response, i18n.t("stamgegevens:errors.niveaus"));
      return data;
    },
  });
}

export function useBezetting() {
  return useQuery({
    queryKey: bezettingKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/beheer/bezetting");
      if (!data)
        throw problem(error, response, i18n.t("stamgegevens:errors.bezetting"));
      return data;
    },
  });
}

export function useCreateFunctie() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      scope: FunctieScope;
      noodzaak: FunctieNoodzaak;
      weergavenaam: string | null;
    }) => {
      const { data, error, response } = await api.POST("/api/beheer/functies", {
        body,
      });
      if (!data)
        throw problem(
          error,
          response,
          i18n.t("stamgegevens:errors.functieToevoegen"),
        );
      return data;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: functiesKey }),
  });
}

export function useUpdateFunctie() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string;
      name: string;
      scope: FunctieScope;
      noodzaak: FunctieNoodzaak;
      weergavenaam: string | null;
    }) => {
      const { data, error, response } = await api.PUT(
        "/api/beheer/functies/{id}",
        {
          params: { path: { id } },
          body,
        },
      );
      if (!data)
        throw problem(
          error,
          response,
          i18n.t("stamgegevens:errors.functieOpslaan"),
        );
      return data;
    },
    // The bezetting too: moving a functie between baan and dag changes which grid it draws in,
    // and its noodzaak changes what a tick in that grid means.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: functiesKey });
      void queryClient.invalidateQueries({ queryKey: bezettingKey });
    },
  });
}

export function useDeleteFunctie() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE(
        "/api/beheer/functies/{id}",
        {
          params: { path: { id } },
        },
      );
      if (error || !response.ok)
        throw problem(
          error,
          response,
          i18n.t("stamgegevens:errors.functieVerwijderen"),
        );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: functiesKey });
      void queryClient.invalidateQueries({ queryKey: bezettingKey });
    },
  });
}

export function useSetBezetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      functieId,
      cellen,
      niveauIds,
    }: {
      functieId: string;
      cellen: { klasseId: string; niveauId: string }[];
      niveauIds: string[];
    }) => {
      const { error, response } = await api.PUT(
        "/api/beheer/bezetting/{functieId}",
        {
          params: { path: { functieId } },
          body: { cellen, niveauIds },
        },
      );
      if (error || !response.ok)
        throw problem(
          error,
          response,
          i18n.t("stamgegevens:errors.bezettingOpslaan"),
        );
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: bezettingKey }),
  });
}

export function useCreateKlasse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      leeftijdsgroepen: Leeftijdsgroep[];
    }) => {
      const { data, error, response } = await api.POST(
        "/api/beheer/stamgegevens/klassen",
        { body },
      );
      if (!data)
        throw problem(
          error,
          response,
          i18n.t("stamgegevens:errors.klasseToevoegen"),
        );
      return data;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: stamKlassenKey }),
  });
}

export function useUpdateKlasse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string;
      name: string;
      leeftijdsgroepen: Leeftijdsgroep[];
    }) => {
      const { data, error, response } = await api.PUT(
        "/api/beheer/stamgegevens/klassen/{id}",
        {
          params: { path: { id } },
          body,
        },
      );
      if (!data)
        throw problem(
          error,
          response,
          i18n.t("stamgegevens:errors.klasseOpslaan"),
        );
      return data;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: stamKlassenKey }),
  });
}

export function useDeleteKlasse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE(
        "/api/beheer/stamgegevens/klassen/{id}",
        {
          params: { path: { id } },
        },
      );
      if (error || !response.ok)
        throw problem(
          error,
          response,
          i18n.t("stamgegevens:errors.klasseVerwijderen"),
        );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: stamKlassenKey });
      void queryClient.invalidateQueries({ queryKey: bezettingKey });
    },
  });
}

export function useCreateNiveau() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string }) => {
      const { data, error, response } = await api.POST(
        "/api/beheer/stamgegevens/niveaus",
        { body },
      );
      if (!data)
        throw problem(
          error,
          response,
          i18n.t("stamgegevens:errors.niveauToevoegen"),
        );
      return data;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: stamNiveausKey }),
  });
}

export function useUpdateNiveau() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; name: string }) => {
      const { data, error, response } = await api.PUT(
        "/api/beheer/stamgegevens/niveaus/{id}",
        {
          params: { path: { id } },
          body,
        },
      );
      if (!data)
        throw problem(
          error,
          response,
          i18n.t("stamgegevens:errors.niveauOpslaan"),
        );
      return data;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: stamNiveausKey }),
  });
}

export function useDeleteNiveau() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE(
        "/api/beheer/stamgegevens/niveaus/{id}",
        {
          params: { path: { id } },
        },
      );
      if (error || !response.ok)
        throw problem(
          error,
          response,
          i18n.t("stamgegevens:errors.niveauVerwijderen"),
        );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: stamNiveausKey });
      void queryClient.invalidateQueries({ queryKey: bezettingKey });
    },
  });
}

export function useBevoegdheden(persoonId: string | null) {
  return useQuery({
    queryKey: bevoegdhedenKey(persoonId ?? ""),
    queryFn: !persoonId
      ? skipToken
      : async () => {
          const { data, error, response } = await api.GET(
            "/api/beheer/bevoegdheden/{persoonId}",
            {
              params: { path: { persoonId } },
            },
          );
          if (!data)
            throw problem(
              error,
              response,
              i18n.t("stamgegevens:errors.bevoegdhedenOphalen"),
            );
          return data;
        },
  });
}

/**
 * Grant and revoke as one mutation keyed on the checkbox. Both are idempotent server-side, so a
 * double click settles on the state the beheerder last asked for rather than toggling twice.
 */
export function useSetBevoegdheid(persoonId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      functieId,
      bevoegd,
    }: {
      functieId: string;
      bevoegd: boolean;
    }) => {
      const path = { persoonId: vereist(persoonId, "persoonId"), functieId };
      const { error, response } = bevoegd
        ? await api.PUT("/api/beheer/bevoegdheden/{persoonId}/{functieId}", {
            params: { path },
          })
        : await api.DELETE("/api/beheer/bevoegdheden/{persoonId}/{functieId}", {
            params: { path },
          });
      if (error || !response.ok)
        throw problem(
          error,
          response,
          i18n.t("stamgegevens:errors.bevoegdheidOpslaan"),
        );
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: bevoegdhedenKey(persoonId ?? ""),
      }),
  });
}

/**
 * The one grid for a klasse at a niveau, or `undefined` where the pair has none. Shared so the
 * count on a tab and the rows in its panel cannot disagree.
 */
export function findStandaardSpeelschema(
  speelschemas: StandaardSpeelschema[] | undefined,
  klasseId: string,
  niveauId: string,
) {
  return speelschemas?.find(
    (s) => s.klasseId === klasseId && s.niveauId === niveauId,
  );
}

/**
 * The eleven standaard grids, whole. A niveau's detail draws one klasse at a time out of this,
 * and eleven pairs of at most thirteen rows is small enough that asking per klasse would be four
 * requests to fill one screen.
 */
export function useStandaardSpeelschemas() {
  return useQuery({
    queryKey: speelschemasKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        "/api/beheer/stamgegevens/speelschemas",
      );
      if (!data)
        throw problem(
          error,
          response,
          i18n.t("stamgegevens:errors.speelschemas"),
        );
      return data;
    },
  });
}

/**
 * One klasse's grid at one niveau, as a whole list. An empty list is a deliberate answer: it
 * takes the standaard away, putting the pair where brandbestrijding op de halve finale already
 * is - a baan there is created empty and says so.
 */
export function useSetStandaardSpeelschema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      klasseId,
      niveauId,
      regels,
    }: {
      klasseId: string;
      niveauId: string;
      regels: StandaardRegel[];
    }) => {
      const { error, response } = await api.PUT(
        "/api/beheer/stamgegevens/speelschemas/{klasseId}/{niveauId}",
        { params: { path: { klasseId, niveauId } }, body: { regels } },
      );
      if (error || !response.ok)
        throw problem(error, response, i18n.t("stamgegevens:errors.opslaan"));
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: speelschemasKey }),
  });
}

/**
 * The three reorder mutations. Each sends the whole sequence, which is what the list is already
 * showing - a swap expressed as two numbers would have to be applied to rows that may have moved.
 */
export function useReorderFuncties() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { error, response } = await api.PUT(
        "/api/beheer/functies/volgorde",
        { body: { ids } },
      );
      if (error || !response.ok)
        throw problem(error, response, i18n.t("stamgegevens:errors.volgorde"));
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: functiesKey }),
  });
}

export function useReorderKlassen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { error, response } = await api.PUT(
        "/api/beheer/stamgegevens/klassen/volgorde",
        {
          body: { ids },
        },
      );
      if (error || !response.ok)
        throw problem(error, response, i18n.t("stamgegevens:errors.volgorde"));
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: stamKlassenKey }),
  });
}

export function useReorderNiveaus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { error, response } = await api.PUT(
        "/api/beheer/stamgegevens/niveaus/volgorde",
        {
          body: { ids },
        },
      );
      if (error || !response.ok)
        throw problem(error, response, i18n.t("stamgegevens:errors.volgorde"));
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: stamNiveausKey }),
  });
}
